/**
 * View filter/sort engine — the PURE core behind saved views.
 *
 * WHY this exists: the power-user teardown flagged that no list surface
 * (accounts, people, tickets, tasks) offers filters, sort, or saved
 * views. This module is the logic half of that feature: a tiny, generic,
 * side-effect-free engine that turns a declarative filter/sort spec into
 * a filtered + ordered row list. Persistence lives in
 * src/lib/db/savedViews.ts; view wiring is a later, gated atom.
 *
 * Design contract:
 * - PURE: no store access, no globals, inputs are never mutated.
 * - Generic: works on any record shape — fields are looked up by name,
 *   so the same engine serves Account, Person, Ticket and ProjectTask.
 * - AND-combined: a row must pass every filter. An empty filter list is
 *   the identity (every row passes), so "no filters" needs no special
 *   casing at the call site.
 * - Honest about missing data: `contains`/`gt`/`lt` never match a row
 *   whose field is absent (no phantom matches), and sorting always puts
 *   missing values last regardless of direction.
 */

/** The comparison operators a single filter can use. */
export type FilterOp = 'eq' | 'neq' | 'contains' | 'gt' | 'lt' | 'in';

/**
 * One declarative filter clause. Filters in a list AND-combine.
 * `value` is deliberately loose (unknown): the engine coerces sensibly
 * per op instead of forcing every caller through a typed builder.
 */
export interface ViewFilter {
  /** Field name looked up directly on the row, e.g. "stage". */
  field: string;
  op: FilterOp;
  value: unknown;
}

/** Declarative sort spec: one field, ascending or descending. */
export interface ViewSort {
  field: string;
  dir: 'asc' | 'desc';
}

/** Read a field off an arbitrary row without assuming its shape. */
function fieldOf(row: unknown, field: string): unknown {
  if (row === null || typeof row !== 'object') return undefined;
  return (row as Record<string, unknown>)[field];
}

/** Does one row satisfy one filter clause? */
function matches(row: unknown, filter: ViewFilter): boolean {
  const actual = fieldOf(row, filter.field);
  const expected = filter.value;

  switch (filter.op) {
    case 'eq':
      return actual === expected;

    case 'neq':
      // A missing field honestly "does not equal" any concrete value.
      return actual !== expected;

    case 'contains': {
      if (Array.isArray(actual)) return actual.includes(expected);
      if (typeof actual === 'string') {
        return actual.toLowerCase().includes(String(expected).toLowerCase());
      }
      return false; // missing / non-container fields never match
    }

    case 'gt':
    case 'lt': {
      // Only compare like-with-like; a missing field never matches.
      const flip = filter.op === 'lt';
      if (typeof actual === 'number' && typeof expected === 'number') {
        return flip ? actual < expected : actual > expected;
      }
      if (typeof actual === 'string' && typeof expected === 'string') {
        return flip ? actual < expected : actual > expected;
      }
      return false;
    }

    case 'in':
      return Array.isArray(expected) && expected.includes(actual);
  }
}

/**
 * Return the rows that pass EVERY filter (AND-combined), in their
 * original order. An empty filter list is the identity. Pure — the
 * input array and its rows are never mutated.
 */
export function applyFilters<T>(rows: T[], filters: ViewFilter[]): T[] {
  if (filters.length === 0) return [...rows];
  return rows.filter((row) => filters.every((f) => matches(row, f)));
}

/**
 * Return a NEW array of rows ordered by the sort spec. Strings compare
 * case-insensitively, numbers numerically; rows missing the field sort
 * last regardless of direction (so a descending value sort doesn't put
 * blanks on top). No spec → identity copy. Pure and stable.
 */
export function applySort<T>(rows: T[], sort?: ViewSort): T[] {
  const out = [...rows];
  if (!sort) return out;

  const { field, dir } = sort;
  const sign = dir === 'desc' ? -1 : 1;

  out.sort((a, b) => {
    const av = fieldOf(a, field);
    const bv = fieldOf(b, field);

    const aMissing = av === undefined || av === null;
    const bMissing = bv === undefined || bv === null;
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1; // missing always last, either direction
    if (bMissing) return -1;

    let cmp = 0;
    if (typeof av === 'number' && typeof bv === 'number') {
      cmp = av - bv;
    } else {
      cmp = String(av).toLowerCase().localeCompare(String(bv).toLowerCase());
    }
    return cmp * sign;
  });

  return out;
}
